import React, { useEffect, useRef, useState } from 'react'
import { Modal, message, Tooltip, Button, Progress } from 'antd'
import {
  DownloadOutlined,
  LinkOutlined,
  CloseOutlined,
  LoadingOutlined,
  EditOutlined,
  StepForwardOutlined
} from '@ant-design/icons'

import './AiVideoModal.scss'
import { VideoPlayer } from '../../../../../../components'
import history from '../../../../../../history'

const AIVideoModal = ({
  visible,
  onClose,
  videoUrl,
  title = 'AI Generated Video',
  onEdit
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false)
  const [showDownloadProgress, setShowDownloadProgress] = useState(false)
  const [downloadPhase, setDownloadPhase] = useState('idle')
  const [downloadBlob, setDownloadBlob] = useState(null)
  const [downloadMeta, setDownloadMeta] = useState({
    totalBytes: 0,
    receivedBytes: 0,
    speedBps: 0,
    error: ''
  })
  const downloadAbortRef = useRef(null)

  useEffect(() => {
    return () => {
      if (downloadAbortRef.current) {
        downloadAbortRef.current.abort()
        downloadAbortRef.current = null
      }
    }
  }, [])

  const handleNavigate = stepKey => {
    if (typeof onEdit === 'function') {
      onEdit(stepKey)
      return
    }

    const idMatch = window.location.pathname.match(/^\/video-create\/([^/]+)/)
    const videoIdFromPath = idMatch?.[1]

    if (videoIdFromPath) {
      history.push(`/video-create/${videoIdFromPath}/${stepKey}`)
      return
    }

    message.info('Navigation unavailable from this context.')
  }

  const formatBytes = bytes => {
    if (!bytes) return '0 MB'
    const mb = bytes / (1024 * 1024)
    return `${mb.toFixed(2)} MB`
  }

  const formatSpeed = speedBps => {
    if (!speedBps) return '0.00 MB/s'
    const mbps = speedBps / (1024 * 1024)
    return `${mbps.toFixed(2)} MB/s`
  }

  const triggerBlobDownload = blob => {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${title.replace(/[^a-zA-Z0-9]/g, '_')}_${Date.now()}.mp4`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const handleDownload = async () => {
    setDownloadLoading(true)
    setDownloadBlob(null)
    setDownloadPhase('downloading')
    setDownloadMeta({
      totalBytes: 0,
      receivedBytes: 0,
      speedBps: 0,
      error: ''
    })
    setShowDownloadProgress(true)

    const abortController = new AbortController()
    downloadAbortRef.current = abortController

    try {
      const response = await fetch(videoUrl, {
        method: 'GET',
        headers: {
          Accept: 'video/mp4,video/webm,video/*'
        },
        signal: abortController.signal
      })

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`)

      const totalBytes = Number(response.headers.get('content-length')) || 0

      if (!response.body || !response.body.getReader) {
        const blob = await response.blob()
        setDownloadBlob(blob)
        setDownloadMeta({
          totalBytes: blob.size || totalBytes,
          receivedBytes: blob.size || totalBytes,
          speedBps: 0,
          error: ''
        })
        setDownloadPhase('ready')
        return
      }

      const reader = response.body.getReader()
      const chunks = []
      let receivedBytes = 0
      let lastBytes = 0
      let lastTick = Date.now()

      setDownloadMeta(prev => ({
        ...prev,
        totalBytes
      }))

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        chunks.push(value)
        receivedBytes += value.length

        const now = Date.now()
        const deltaSeconds = (now - lastTick) / 1000

        if (deltaSeconds >= 0.25) {
          const speedBps = (receivedBytes - lastBytes) / deltaSeconds
          setDownloadMeta(prev => ({
            ...prev,
            receivedBytes,
            speedBps
          }))
          lastTick = now
          lastBytes = receivedBytes
        } else {
          setDownloadMeta(prev => ({
            ...prev,
            receivedBytes
          }))
        }
      }

      const blob = new Blob(chunks, {
        type: response.headers.get('content-type') || 'video/mp4'
      })
      setDownloadBlob(blob)
      setDownloadMeta(prev => ({
        ...prev,
        totalBytes: totalBytes || blob.size,
        receivedBytes: blob.size
      }))
      setDownloadPhase('ready')
    } catch (error) {
      if (abortController.signal.aborted) {
        setDownloadPhase('idle')
        return
      }

      console.error('Download failed:', error)
      setDownloadPhase('error')
      setDownloadMeta(prev => ({
        ...prev,
        error: error?.message || 'Failed to prepare download'
      }))
    } finally {
      downloadAbortRef.current = null
      setDownloadLoading(false)
    }
  }

  const handleConfirmDownload = () => {
    setShowDownloadConfirm(false)
    handleDownload()
  }

  const handleCloseDownloadProgress = () => {
    if (downloadAbortRef.current) {
      downloadAbortRef.current.abort()
      downloadAbortRef.current = null
    }
    setShowDownloadProgress(false)
    setDownloadBlob(null)
    setDownloadPhase('idle')
    setDownloadMeta({
      totalBytes: 0,
      receivedBytes: 0,
      speedBps: 0,
      error: ''
    })
  }

  const handleSaveDownload = () => {
    if (!downloadBlob) return
    triggerBlobDownload(downloadBlob)
    message.success('Video downloaded successfully!')
    handleCloseDownloadProgress()
  }

  const handleClosePlayer = () => {
    if (downloadAbortRef.current) {
      downloadAbortRef.current.abort()
      downloadAbortRef.current = null
    }
    setDownloadLoading(false)
    setShowDownloadConfirm(false)
    setShowDownloadProgress(false)
    setDownloadPhase('idle')
    setDownloadBlob(null)
    onClose()
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(videoUrl)
      message.success('Video link copied to clipboard!')
    } catch (err) {
      console.error('Copy failed:', err)
      message.error('Failed to copy link')
    }
  }

  return (
    <Modal
      visible={visible}
      onCancel={handleClosePlayer}
      footer={null}
      width="90vw"
      centered
      className="ai-video-modal"
      closable={false}
      bodyStyle={{ padding: 0 }}
      destroyOnClose
    >
      <div className="ai-viewer-layout">
        {/* Top Section: Dedicated Header Bar */}
        <div className="viewer-header">
          <div className="video-meta">
            <h2 className="video-title">{title}</h2>
            <span className="video-badge">AI GENERATED</span>
          </div>

          <div className="viewer-actions">
            <Tooltip title="Copy Link" placement="bottom">
              <button className="action-button" onClick={handleCopyLink}>
                <LinkOutlined />
              </button>
            </Tooltip>

            <Tooltip title="Download" placement="bottom">
              <button
                className="action-button"
                onClick={() => setShowDownloadConfirm(true)}
                disabled={downloadLoading}
              >
                {downloadLoading ? <LoadingOutlined /> : <DownloadOutlined />}
              </button>
            </Tooltip>

            <div className="action-divider"></div>

            <Tooltip title="Edit Content" placement="bottom">
              <button
                className="action-button"
                onClick={() => handleNavigate('uploadDocument')}
              >
                <EditOutlined />
              </button>
            </Tooltip>

            <Tooltip title="Go to Final Step" placement="bottom">
              <button
                className="action-button"
                onClick={() => handleNavigate('finalVideo')}
              >
                <StepForwardOutlined />
              </button>
            </Tooltip>

            <div className="action-divider"></div>

            <Tooltip title="Close Player" placement="bottom">
              <button className="close-button" onClick={handleClosePlayer}>
                <CloseOutlined />
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Bottom Section: Video Stage (No overlays) */}
        <div className="viewer-stage">
          {/* Loading Indicator */}
          {isLoading && (
            <div className="loading-stage">
              <div className="custom-loader">
                <div className="loader-content">
                  <h3 className="loader-title">Preparing Video</h3>
                  <p className="loader-subtitle">
                    Crafting your AI masterpiece...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Video Player */}
          {/* <ReactPlayer
            key={videoUrl}
            url={videoUrl ? videoUrl.split('#')[0] : ''}
            width="100%"
            height="100%"
            playing
            controls
            onReady={() => setIsLoading(false)}
            onError={e => {
              console.error('Video player error details:', e)
              const videoElement = document.querySelector(
                '.ai-viewer-layout video'
              )
              if (videoElement) {
                videoElement.removeAttribute('crossorigin')
              }
              setIsLoading(false)
            }}
            config={{
              file: {
                forceVideo: true,
                attributes: {
                  controlsList: 'nodownload',
                  disablePictureInPicture: true
                }
              }
            }}
          /> */}

          <VideoPlayer
            src={videoUrl}
            autoPlay
            controls
            onReady={() => setIsLoading(false)}
            onError={e => {
              console.error('Video player error details:', e)
              const videoElement = document.querySelector(
                '.ai-viewer-layout video'
              )
              if (videoElement) {
                videoElement.removeAttribute('crossorigin')
              }
              setIsLoading(false)
            }}
          />
        </div>
      </div>

      <Modal
        visible={showDownloadConfirm}
        onCancel={() => setShowDownloadConfirm(false)}
        onOk={handleConfirmDownload}
        title="Download Video"
        okText="Start Download"
        cancelText="Cancel"
        centered
        maskClosable={false}
      >
        <p>
          This will start preparing the video download. You&apos;ll see live
          progress and can save it once ready.
        </p>
      </Modal>

      <Modal
        visible={showDownloadProgress}
        onCancel={handleCloseDownloadProgress}
        footer={null}
        title="Download Status"
        centered
        maskClosable={false}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <strong>Status:</strong>{' '}
            {downloadPhase === 'downloading'
              ? 'Preparing download...'
              : downloadPhase === 'ready'
              ? 'Ready to save'
              : downloadPhase === 'error'
              ? 'Failed'
              : 'Idle'}
          </div>

          <Progress
            percent={
              downloadMeta.totalBytes > 0
                ? Math.min(
                    100,
                    Math.round(
                      (downloadMeta.receivedBytes / downloadMeta.totalBytes) *
                        100
                    )
                  )
                : undefined
            }
            status={downloadPhase === 'error' ? 'exception' : 'active'}
          />

          <div>
            <strong>Downloaded:</strong> {formatBytes(downloadMeta.receivedBytes)}
            {downloadMeta.totalBytes > 0 &&
              ` / ${formatBytes(downloadMeta.totalBytes)}`}
          </div>

          <div>
            <strong>Speed:</strong> {formatSpeed(downloadMeta.speedBps)}
          </div>

          {downloadPhase === 'error' && (
            <div style={{ color: '#ff4d4f' }}>
              {downloadMeta.error || 'Unable to prepare download.'}
            </div>
          )}

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              marginTop: 8
            }}
          >
            <Button onClick={handleCloseDownloadProgress}>Close</Button>
            <Button
              type="primary"
              onClick={handleSaveDownload}
              disabled={downloadPhase !== 'ready' || !downloadBlob}
            >
              Save Video
            </Button>
          </div>
        </div>
      </Modal>
    </Modal>
  )
}

export default AIVideoModal
